import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ColumnConfig, ColumnGroup, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { ProgressBar } from "@app/shared/ProgressBar";
import { AdequacyProgressBarLabels } from "@app/shared/progressBarBand";
import { NetworkSubmissionContext } from "@app/compliance-connect/network-submission/NetworkSubmissionContext";
import { useDebouncedValue } from "@app/shared/hooks/useDebouncedValue";
import {  AdequacyReviewRowData, NetworkSubmissionSummary, AdequacyReviewTableRow } from "@app/compliance-connect/network-submission/interfaces";
import NetworkSubmissionService from "@app/compliance-connect/network-submission/networkSubmissionService";
import { ProjectPerspective } from "@app/projects/enumerators";
import { getProjectPerspectiveDescription } from "@app/projects/projectPerspectiveConversions";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import { QESButton } from "@app/shared/qes-standards/Button";
import { useQuery } from "@tanstack/react-query";
import { createSkeletonRows } from "@app/compliance-connect/network-submission/networkSubmissionHelper";


const perspectiveDropdownItems = [ProjectPerspective.ByCounty, ProjectPerspective.BySpecialty].map((perspective) => ({
  key: perspective,
  name: getProjectPerspectiveDescription(perspective),
}));

const BASE_COLUMN_CONFIG: ColumnConfig = {
  accessMet: { displayText: "Met", sizeContentToFit: true, sortOrder: 8, testId: "ns-adequacy-review__access-met-col", },
  accessNotMet: { displayText: "Not Met", sizeContentToFit: true, sortOrder: 9, testId: "ns-adequacy-review__access-not-met-col" },
  adequacyGraph: { displayText: "Adequacy", minWidth: "180px", noSort: true, sortOrder: 3, testId: "ns-adequacy-review__adequacy-graph-col" },
  adequacyIndexString: { displayText: "Adequacy Index", sizeContentToFit: true, sortOrder: 2, testId: "ns-adequacy-review__adequacy-index-col" },
  exceptionsApproved: { displayText: "Approved", sizeContentToFit: true, sortOrder: 7, testId: "ns-adequacy-review__exceptions-approved-col" },
  exceptionsTotal: { displayText: "Total", sizeContentToFit: true, sortOrder: 6, testId: "ns-adequacy-review__exceptions-total-col" },
  gapsFillable: { displayText: "Fillable", sizeContentToFit: true, sortOrder: 5, testId: "ns-adequacy-review__gaps-fillable-col" },
  gapsTotal: { displayText: "Total", sizeContentToFit: true, sortOrder: 4, testId: "ns-adequacy-review__gaps-total-col" },
  membershipAtRiskCountString: { displayText: "#", sizeContentToFit: true, sortOrder: 14, testId: "ns-adequacy-review__mat-count-col" },
  membershipAtRiskPercentString: { displayText: "%", sizeContentToFit: true, sortOrder: 15, testId: "ns-adequacy-review__mat-percent-col" },
  name: { displayText: "County", sortOrder: 1, style: { width: "25%" },  testId: "ns-adequacy-review__name-col" },
  servicingMet: { displayText: "Met", sizeContentToFit: true, sortOrder: 11, testId: "ns-adequacy-review__servicing-met-col" },
  servicingNotMet: { displayText: "Not Met", sizeContentToFit: true, sortOrder: 12, testId: "ns-adequacy-review__servicing-not-met-col" },
};

const COLUMN_GROUPS: ColumnGroup<AdequacyReviewTableRow>[] = [
  { children: ["name"], hideKey: true, key: " ", testId: "ns-adequacy-review__name-group" },
  { children: ["adequacyGraph", "adequacyIndexString"], key: "Adequacy", testId: "ns-adequacy-review__adequacy-group" },
  { children: ["gapsTotal", "gapsFillable"], key: "Gaps", testId: "ns-adequacy-review__gaps-group" },
  { children: ["exceptionsTotal", "exceptionsApproved"], key: "Exceptions", testId: "ns-adequacy-review__exceptions-group" },
  { children: ["accessMet", "accessNotMet"], key: "Access", testId: "ns-adequacy-review__access-group" },
  { children: ["servicingMet", "servicingNotMet"], key: "Servicing", testId: "ns-adequacy-review__servicing-group" },
  { children: ["membershipAtRiskCountString", "membershipAtRiskPercentString"], key: "Membership at Risk", testId: "ns-adequacy-review__mat-group" },
];

const toTableRow = (row: AdequacyReviewRowData, perspective: ProjectPerspective, isParentRow: boolean): AdequacyReviewTableRow => ({
  ...row,
  adequacyGraph: (
    <div style={{ minWidth: 200 }}>
      <ProgressBar
        bands={[
          { color: "progress-bar-success", colorType: "class", label: AdequacyProgressBarLabels.MetBothCriteria, outOfScale: false, percent: row?.percentPassBoth ?? 0 },
          { color: "progress-bar-warning", colorType: "class", label: AdequacyProgressBarLabels.MetOneCriteria, outOfScale: false, percent: row?.percentPassOne ?? 0 },
          { color: "progress-bar-danger", colorType: "class", label: AdequacyProgressBarLabels.MetNoCriteria, outOfScale: false, percent: row?.percentPassNone ?? 0 },
          { color: "progress-bar-info", colorType: "class", label: AdequacyProgressBarLabels.ApprovedException, outOfScale: false, percent: row?.percentPassByException ?? 0 },
        ]}
        isGrayscale={false}
      />
    </div>
  ),
  adequacyIndexString: row.adequacyIndex.toFixed(1),
  membershipAtRiskCountString: perspective === ProjectPerspective.ByCounty && isParentRow ? "---" : row.membershipAtRiskCount.toString(),
  membershipAtRiskPercentString: perspective === ProjectPerspective.ByCounty && isParentRow ? "---" : row.membershipAtRiskPercent.toFixed(1),
  [wafflesKeyColumnName]: row.id,
});

// Mirrors Waffles' own internal sort (Waffles.tsx handleInternalSort) so behavior is unchanged;
// lifted to parent state so it survives the key-based remount used for shouldAutoExpand.
const sortAdequacyReviewRows = (
  rows: AdequacyReviewTableRow[],
  column: keyof AdequacyReviewTableRow | undefined,
  direction: SortDirections,
): AdequacyReviewTableRow[] => {
  if (!column || direction === SortDirections.None) return rows;
  const multiplier = direction === SortDirections.Asc ? 1 : -1;
  return [...rows].sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier);
};

interface NetworkSubmissionStepAdequacyReviewProps {
  setSummary: (summary: NetworkSubmissionSummary) => void;
}


export const NetworkSubmissionStepAdequacyReview: FC<NetworkSubmissionStepAdequacyReviewProps> = ({ setSummary }) => {
  const wizardContext = useContext(NetworkSubmissionContext);
  const [countyFilter, setCountyFilter] = useState<DropdownItem<string>[] | null>(null);
  const [specialtyFilter, setSpecialtyFilter] = useState<DropdownItem<string>[] | null>(null);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [perspective, setPerspective] = useState(ProjectPerspective.ByCounty);
  const [expansionCache, setExpansionCache] = useState<Map<string, AdequacyReviewTableRow[]>>(new Map());
  const [sortedColumn, setSortedColumn] = useState<keyof AdequacyReviewTableRow | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<SortDirections>(SortDirections.None);

  const networkSubmissionService = useRef(new NetworkSubmissionService()).current;

  const countyOptionsRef = useRef<DropdownItem<string>[]>([]);
  const specialtyOptionsRef = useRef<DropdownItem<string>[]>([]);

  const countyIds = useMemo(() => {
    if (countyFilter === null) return [];
    const total = countyOptionsRef.current.length;
    if (total > 0 && countyFilter.length === total) return [];
    return countyFilter.map(c => c.key);
  }, [countyFilter]);

  const specialtyIds = useMemo(() => {
    if (specialtyFilter === null) return [];
    const total = specialtyOptionsRef.current.length;
    if (total > 0 && specialtyFilter.length === total) return [];
    return specialtyFilter.map(s => s.key);
  }, [specialtyFilter]);

  const debouncedCountyIds = useDebouncedValue(countyIds, 1000);
  const debouncedSpecialtyIds = useDebouncedValue(specialtyIds, 1000);

  const { data: reviewData, isLoading } = useQuery({
    queryFn: ({ signal }) => networkSubmissionService.getAdequacyReview(wizardContext.selectedProject!.id, debouncedCountyIds, debouncedSpecialtyIds, signal),
    queryKey: ["ns-adequacy-review", wizardContext.selectedProject?.id, debouncedCountyIds, debouncedSpecialtyIds],
  });
  
  useEffect(() => {
    if (reviewData) {
      setSummary(reviewData.summary);
    }
  }, [reviewData, setSummary]);

  const countyDropdownItems = useMemo<DropdownItem<string>[]>(() => {
    if (reviewData && debouncedCountyIds.length === 0 && debouncedSpecialtyIds.length === 0) {
      countyOptionsRef.current = reviewData.counties
        .map(c => ({ key: c.id, name: c.name }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    return countyOptionsRef.current;
  }, [reviewData, debouncedCountyIds, debouncedSpecialtyIds]);

  const specialtyDropdownItems = useMemo<DropdownItem<string>[]>(() => {
    if (reviewData && debouncedCountyIds.length === 0 && debouncedSpecialtyIds.length === 0) {
      specialtyOptionsRef.current = reviewData.specialties
        .map(s => ({ key: s.id, name: s.name }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    return specialtyOptionsRef.current;
  }, [reviewData, debouncedCountyIds, debouncedSpecialtyIds]);

  const parentRows = useMemo<AdequacyReviewRowData[]>(() => {
    if (!reviewData)
      return [];

    expansionCache.clear();
    return perspective === ProjectPerspective.ByCounty ? reviewData.counties : reviewData.specialties;
  }, [reviewData, perspective]);

  const tableData = useMemo<AdequacyReviewTableRow[]>(
    () => isLoading ? createSkeletonRows<AdequacyReviewTableRow>(10, BASE_COLUMN_CONFIG) : parentRows.map(row => toTableRow(row, perspective, true)),
    [isLoading, parentRows, perspective]
  );

  const sortedTableData = useMemo<AdequacyReviewTableRow[]>(
    () => sortAdequacyReviewRows(tableData, sortedColumn, sortDirection),
    [tableData, sortedColumn, sortDirection]
  );

  const handleSort = (
    _: AdequacyReviewTableRow[],
    column: keyof AdequacyReviewTableRow,
    direction: SortDirections,
  ): AdequacyReviewTableRow[] => {
    setSortedColumn(column);
    setSortDirection(direction);
    return _;
  };

  const shouldAutoExpand =
    (perspective === ProjectPerspective.ByCounty && debouncedSpecialtyIds.length > 0) ||
    (perspective === ProjectPerspective.BySpecialty && debouncedCountyIds.length > 0);

  const dataExpansions = useMemo<Map<string, AdequacyReviewTableRow[]>>(() => {
    const map = new Map<string, AdequacyReviewTableRow[]>();
    parentRows.forEach(row => {
      if (expansionCache.has(row.id)) {
        map.set(row.id, expansionCache.get(row.id) ?? []);
      } else if (shouldAutoExpand && reviewData) {
        const children = (perspective === ProjectPerspective.ByCounty
          ? reviewData.specialtiesByCounty[row.id]
          : reviewData.countiesBySpecialty[row.id]) ?? [];
        map.set(row.id, children.map(child => toTableRow(child, perspective, false)));
      } else {
        map.set(row.id, []);
      }
    });
    return map;
  }, [parentRows, expansionCache, shouldAutoExpand, reviewData, perspective]);

  const handleExpansionToggle = useCallback(async (expansionKey: string): Promise<void> => {
    if (!reviewData)
      return;

    setLoadingChildren(true);
    setExpansionCache(prev => new Map(prev).set(expansionKey, createSkeletonRows<AdequacyReviewTableRow>(3, BASE_COLUMN_CONFIG)));

    setTimeout(() => {
      const children = (perspective === ProjectPerspective.ByCounty
        ? (reviewData.specialtiesByCounty[expansionKey] ?? [])
        : (reviewData.countiesBySpecialty[expansionKey] ?? [])
      ).map(row => toTableRow(row, perspective, false));
      setExpansionCache(prev => new Map(prev).set(expansionKey, children));
      setLoadingChildren(false);
    }, 0);
  }, [reviewData, perspective]);

  const columnConfig = useMemo<ColumnConfig>(() => ({
    ...BASE_COLUMN_CONFIG,
    name: { ...BASE_COLUMN_CONFIG.name, displayText: perspective === ProjectPerspective.ByCounty ? "County" : "Specialty" },
  }), [perspective]);


  const handleClear = () => {
    setCountyFilter(null);
    setSpecialtyFilter(null);
  };


  return (
    <>
      <fieldset className="qes-searchbox bg-white d-flex align-items-end gap-4 mt-4 p-4">
        <MultiselectDropdown<string>
          containerClassName="ns-adequacy-review__county-dropdown w-25 me-3"
          items={countyDropdownItems}
          label="County"
          onSelectionChanged={setCountyFilter}
          selectedItems={countyFilter ?? countyDropdownItems}
          testId="ns-adequacy-review__county-filter"
        />
        <MultiselectDropdown<string>
          containerClassName="ns-adequacy-review__specialty-dropdown w-25 me-3"
          items={specialtyDropdownItems}
          label="Specialty"
          onSelectionChanged={setSpecialtyFilter}
          selectedItems={specialtyFilter ?? specialtyDropdownItems}
          testId="ns-adequacy-review__specialty-filter"
        />
        <QESButton
          qesType="secondary"
          qesTestId="ns-adequacy-review__clear-link"
          className="btn-link"
          onClick={handleClear}
        >
          Clear All
        </QESButton>
      </fieldset>

      <NewActionMenu
        buttonText={`View by: ${perspectiveDropdownItems.find((item) => item.key === perspective)?.name}`}
        testId="ns-adequacy-review__view-by-dropdown"
        buttonClassNames="qes-button-secondary"
        closeOnClick={true}
        loading={isLoading}
        containerClassnames="m-2"
      >
        {perspectiveDropdownItems.map((item) => (
          <NewActionMenuItem
            display={item.name}
            isSelected={item.key === perspective}
            key={item.key}
            onClick={() => setPerspective(item.key)}
            testId={`ns-adequacy-review__view-by-option--${item.key}`}
          />
        ))}
      </NewActionMenu>

      <Waffles<AdequacyReviewTableRow>
        key={shouldAutoExpand ? "ns-adequacy-review__expanded" : "ns-adequacy-review__collapsed"}
        loading={isLoading || loadingChildren}
        columns={columnConfig}
        columnGroups={COLUMN_GROUPS}
        customSort={handleSort}
        data={sortedTableData}
        dataExpansions={dataExpansions}
        dataExpansionOnToggleClick={handleExpansionToggle}
        dataExpansionsDefaultOpen={shouldAutoExpand}
        enablePagination={false}
        fullWidth={true}
        passThroughSorting={true}
        showCheckboxes={false}
        sortDirectionOverride={sortDirection}
        sortedColumnOverride={sortedColumn}
        testId="ns-adequacy-review__table"
      />
    </>
  );
};
